import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Briefcase,
  CheckCircle2,
  AlertCircle,
  PenTool,
  Laptop,
  Phone,
  KeySquare,
  Boxes,
  PackageOpen,
} from "lucide-react";

const CAT_LABEL = {
  communication: "Communication",
  it: "IT Equipment",
  access: "Access & Accounts",
  optional: "Optional Assets",
  custom: "Other",
};
const CAT_ICON = {
  communication: Phone,
  it: Laptop,
  access: KeySquare,
  optional: Boxes,
  custom: PackageOpen,
};
const STATUS_BADGE = {
  draft: "bg-slate-200 text-slate-700",
  pending_employee_ack: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  return_in_progress: "bg-sky-100 text-sky-800",
  closed: "bg-slate-300 text-slate-800",
};

export default function MyAssetHandovers() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [sig, setSig] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/employee-assets/employee/my");
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load asset handovers");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const ack = async () => {
    if (sig.trim().length < 2) {
      toast.error("Type your full name to e-sign");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/employee-assets/employee/${active.id}/ack`, {
        signature_name: sig.trim(),
        remarks: remarks || null,
      });
      toast.success("Acknowledged — thank you!");
      setActive(null);
      setSig("");
      setRemarks("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (active) {
    const allItems = [
      ...(active.items || []),
      ...(active.custom_items || []),
    ];
    const issued = allItems.filter((it) => it.status === "issued");
    const pending = active.status === "pending_employee_ack";
    return (
      <div className="space-y-4 pb-32" data-testid="employee-asset-detail">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActive(null)}
            className="text-slate-500 text-sm hover:underline"
          >
            ← Back
          </button>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Asset Handover · {active.handover_date}
          </div>
          <h1
            className="text-xl font-bold text-slate-900"
            style={{ fontFamily: "Chivo" }}
          >
            {issued.length} item(s) issued
          </h1>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Issued by {active.issued_by_name || "—"} · Approved by{" "}
            {active.approver_name || "—"}
          </p>
        </div>

        {/* Items grouped */}
        {["communication", "it", "access", "optional", "custom"].map((cat) => {
          const list = allItems.filter(
            (it) => it.category === cat && it.status === "issued",
          );
          if (list.length === 0) return null;
          const Ic = CAT_ICON[cat];
          return (
            <div key={cat} className="rounded-xl bg-white ring-1 ring-slate-200">
              <div className="bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-600 flex items-center gap-1.5 rounded-t-xl">
                <Ic className="h-3 w-3" /> {CAT_LABEL[cat]}
              </div>
              <div className="divide-y divide-slate-100">
                {list.map((it) => (
                  <div
                    key={it.key}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <div>
                      <div className="font-semibold">{it.label}</div>
                      {it.value && (
                        <div className="text-[11px] text-slate-500 font-mono">
                          {it.value}
                        </div>
                      )}
                      {it.notes && (
                        <div className="text-[11px] text-slate-500">
                          {it.notes}
                        </div>
                      )}
                    </div>
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {pending ? (
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">
              Declaration & E-Sign
            </div>
            <div className="text-[11px] text-slate-600 rounded-lg bg-slate-50 p-2">
              I acknowledge receiving the above assets in good condition and
              understand that these remain company property and shall be
              returned upon transfer, replacement, or separation.
            </div>
            <label className="block">
              <span className="text-[11px] text-slate-600">
                Type your full name *
              </span>
              <input
                value={sig}
                onChange={(e) => setSig(e.target.value)}
                placeholder="Your full name"
                className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm"
                data-testid="emp-asset-sig"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-600">
                Remarks (optional)
              </span>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-40">
              <button
                disabled={busy}
                onClick={ack}
                data-testid="emp-asset-ack"
                className="w-full max-w-md mx-auto block rounded-xl bg-slate-900 text-white py-3 font-bold disabled:bg-slate-400 flex items-center justify-center gap-2"
              >
                <PenTool className="h-4 w-4" />
                {busy ? "Submitting…" : "Acknowledge & Sign"}
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-3 text-xs text-emerald-900">
            <CheckCircle2 className="h-4 w-4 inline mr-1" />
            Acknowledged by{" "}
            <strong>{active.employee_signature_name}</strong> on{" "}
            {active.employee_acknowledged_at?.slice(0, 10)}
          </div>
        )}
      </div>
    );
  }

  const pendingCount = rows.filter(
    (r) => r.status === "pending_employee_ack",
  ).length;
  return (
    <div className="space-y-4" data-testid="my-asset-handovers">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <Briefcase className="h-3.5 w-3.5" /> My Records
        </div>
        <h1
          className="text-xl font-bold text-slate-900"
          style={{ fontFamily: "Chivo" }}
        >
          Asset Handovers
        </h1>
      </div>
      {pendingCount > 0 && (
        <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
          <AlertCircle className="h-4 w-4 inline mr-1" />
          <strong>{pendingCount} pending</strong> — please acknowledge to
          complete onboarding.
        </div>
      )}
      <div className="space-y-2">
        {rows.length === 0 ? (
          <div className="rounded-xl bg-white ring-1 ring-slate-200 p-6 text-center text-sm text-slate-500">
            No asset handovers yet.
          </div>
        ) : (
          rows.map((r) => (
            <button
              key={r.id}
              onClick={() => setActive(r)}
              data-testid={`emp-asset-row-${r.id}`}
              className="w-full rounded-2xl bg-white ring-1 ring-slate-200 p-4 text-left active:scale-[0.99] transition flex items-center gap-3"
            >
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-slate-700" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">
                  {r.issued_count || 0} item(s) issued
                </div>
                <div className="text-[11px] text-slate-500">
                  {r.handover_date} · Issued by {r.issued_by_name || "—"}
                </div>
              </div>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  STATUS_BADGE[r.status] || "bg-slate-100"
                }`}
              >
                {r.status === "pending_employee_ack"
                  ? "ACTION NEEDED"
                  : r.status}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
